import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import IncomeParametersSection from './IncomeParametersSection';
import TaxResidencySection from './TaxResidencySection';
import DeductionsSection from './DeductionsSection';
import EntrepreneurSection from './EntrepreneurSection';
import SelfEmployedSection from './SelfEmployedSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент основной формы расчета НДФЛ
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  taxDetails,
  setTaxDetails,
  incomeTypes,
  taxRates,
  deductionLimits,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalcName, setSaveCalcName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обработка взаимозависимых полей
      if (name === 'includeDeductions' && !checked) {
        // Сбрасываем поля вычетов при отключении
        newState.hasChildren = false;
        newState.childrenCount = '1';
        newState.hasChildrenDisabled = false;
        newState.childrenDisabledCount = '0';
        newState.hasIpn = false;
        newState.ipnAmount = '0';
        newState.hasMortgage = false;
        newState.mortgageInterestAmount = '0';
        newState.hasEducation = false;
        newState.educationExpenseAmount = '0';
        newState.hasMedical = false;
        newState.medicalExpenseAmount = '0';
        newState.hasInsurance = false;
        newState.insuranceExpenseAmount = '0';
        newState.hasInvestment = false;
        newState.investmentExpenseAmount = '0';
        newState.hasCharity = false;
        newState.charityExpenseAmount = '0';
      }
      
      // Обработка поля налогового резиденства
      if (name === 'taxResidency' && value === 'nonresident') {
        // Для нерезидентов некоторые вычеты не применяются
        newState.includeDeductions = false;
      }
      
      // Обработка изменения статуса ИП
      if (name === 'isIp') {
        if (checked) {
          newState.incomeType = 'business';
        } else {
          newState.ipTaxSystem = 'osn';
          newState.ipExpenses = '0';
        }
      }
      
      // Обработка изменения статуса самозанятого
      if (name === 'isSelfEmployed') {
        if (checked) {
          newState.isIp = false; // Нельзя быть одновременно ИП и самозанятым
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      incomeAmount, incomeType, paymentPeriod, taxResidency,
      includeDeductions, hasChildren, childrenCount, hasChildrenDisabled, childrenDisabledCount,
      hasIpn, ipnAmount, hasMortgage, mortgageInterestAmount,
      hasEducation, educationExpenseAmount, hasMedical, medicalExpenseAmount,
      hasInsurance, insuranceExpenseAmount, hasInvestment, investmentExpenseAmount,
      hasCharity, charityExpenseAmount, isIp, ipTaxSystem, ipExpenses, isSelfEmployed
    } = formData;

    // Проверка суммы дохода
    const numIncomeAmount = parseFloatOrDefault(incomeAmount, -1);
    if (numIncomeAmount <= 0) newErrors.incomeAmount = 'Сумма дохода должна быть положительной.';
    if (!newErrors.incomeAmount && numIncomeAmount > 1000000000) newErrors.incomeAmount = 'Максимальный доход для расчёта - 1 млрд ₽.';

    // Проверка типа дохода
    if (!incomeType) newErrors.incomeType = 'Выберите тип дохода.';

    // Проверка периода платежа
    if (!paymentPeriod) newErrors.paymentPeriod = 'Выберите период дохода.';

    // Проверка налогового резидентства
    if (!taxResidency) newErrors.taxResidency = 'Выберите налоговый статус.';

    // Проверка полей для вычетов (если включены)
    if (includeDeductions) {
      // Проверка стандартных вычетов на детей
      if (hasChildren) {
        const numChildrenCount = parseIntOrDefault(childrenCount, 0);
        if (numChildrenCount <= 0) newErrors.childrenCount = 'Количество детей должно быть положительным числом.';
        if (!newErrors.childrenCount && numChildrenCount > 20) newErrors.childrenCount = 'Пожалуйста, введите реалистичное количество детей.';
      }

      if (hasChildrenDisabled) {
        const numChildrenDisabledCount = parseIntOrDefault(childrenDisabledCount, 0);
        if (numChildrenDisabledCount <= 0) newErrors.childrenDisabledCount = 'Количество детей-инвалидов должно быть положительным числом.';
        if (!newErrors.childrenDisabledCount && numChildrenDisabledCount > 20) newErrors.childrenDisabledCount = 'Пожалуйста, введите реалистичное количество детей.';
      }

      // Проверка социальных вычетов
      if (hasIpn) {
        const numIpnAmount = parseFloatOrDefault(ipnAmount, -1);
        if (numIpnAmount <= 0) newErrors.ipnAmount = 'Сумма взносов должна быть положительной.';
        if (!newErrors.ipnAmount && numIpnAmount > 400000) newErrors.ipnAmount = 'Максимальный вычет на ИПН - 400 000 ₽.';
      }

      if (hasMortgage) {
        const numMortgageAmount = parseFloatOrDefault(mortgageInterestAmount, -1);
        if (numMortgageAmount <= 0) newErrors.mortgageInterestAmount = 'Сумма процентов должна быть положительной.';
        if (!newErrors.mortgageInterestAmount && numMortgageAmount > 3000000) newErrors.mortgageInterestAmount = 'Максимальный вычет по процентам - 3 000 000 ₽.';
      }

      if (hasEducation) {
        const numEducationAmount = parseFloatOrDefault(educationExpenseAmount, -1);
        if (numEducationAmount <= 0) newErrors.educationExpenseAmount = 'Сумма расходов должна быть положительной.';
        if (!newErrors.educationExpenseAmount && numEducationAmount > 50000) newErrors.educationExpenseAmount = 'Максимальный вычет на обучение - 50 000 ₽.';
      }

      if (hasMedical) {
        const numMedicalAmount = parseFloatOrDefault(medicalExpenseAmount, -1);
        if (numMedicalAmount <= 0) newErrors.medicalExpenseAmount = 'Сумма расходов должна быть положительной.';
        if (!newErrors.medicalExpenseAmount && numMedicalAmount > 120000) newErrors.medicalExpenseAmount = 'Максимальный общий вычет на лечение в составе социальных вычетов - 120 000 ₽.';
      }

      if (hasInsurance) {
        const numInsuranceAmount = parseFloatOrDefault(insuranceExpenseAmount, -1);
        if (numInsuranceAmount <= 0) newErrors.insuranceExpenseAmount = 'Сумма расходов должна быть положительной.';
        if (!newErrors.insuranceExpenseAmount && numInsuranceAmount > 120000) newErrors.insuranceExpenseAmount = 'Максимальный вычет на страхование - 120 000 ₽.';
      }

      if (hasInvestment) {
        const numInvestmentAmount = parseFloatOrDefault(investmentExpenseAmount, -1);
        if (numInvestmentAmount <= 0) newErrors.investmentExpenseAmount = 'Сумма инвестиций должна быть положительной.';
        if (!newErrors.investmentExpenseAmount && numInvestmentAmount > 400000) newErrors.investmentExpenseAmount = 'Максимальный вычет на ИИС - 400 000 ₽.';
      }

      if (hasCharity) {
        const numCharityAmount = parseFloatOrDefault(charityExpenseAmount, -1);
        if (numCharityAmount <= 0) newErrors.charityExpenseAmount = 'Сумма пожертвований должна быть положительной.';
        const maxCharityDeduction = numIncomeAmount * 0.25;
        if (!newErrors.charityExpenseAmount && numCharityAmount > maxCharityDeduction) newErrors.charityExpenseAmount = `Максимальный вычет на благотворительность - 25% от дохода (${formatCurrency(maxCharityDeduction)}).`;
      }
    }

    // Проверка полей для ИП
    if (isIp) {
      if (!ipTaxSystem) newErrors.ipTaxSystem = 'Выберите систему налогообложения.';
      
      if (ipTaxSystem === 'usn_income_expenses') {
        const numIpExpenses = parseFloatOrDefault(ipExpenses, -1);
        if (numIpExpenses < 0) newErrors.ipExpenses = 'Сумма расходов не может быть отрицательной.';
        if (!newErrors.ipExpenses && numIpExpenses > numIncomeAmount) newErrors.ipExpenses = 'Расходы не могут превышать доходы.';
      }
    }

    // Проверка на взаимоисключающие статусы
    if (isIp && isSelfEmployed) {
      newErrors.isSelfEmployed = 'Нельзя быть одновременно ИП и самозанятым.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Основная функция расчета НДФЛ
  const calculateIncomeTax = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setTaxDetails(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numIncomeAmount = parseFloatOrDefault(formData.incomeAmount);
        
        // Нормализуем доход к годовому (если указан иначе)
        let annualIncome = numIncomeAmount;
        if (formData.paymentPeriod === 'monthly') {
          annualIncome = numIncomeAmount * 12;
        } else if (formData.paymentPeriod === 'daily') {
          annualIncome = numIncomeAmount * 260; // примерное количество рабочих дней в году
        }

        // Определяем применимую налоговую ставку
        let taxRate = 13; // Ставка по умолчанию
        
        // Расчет налога в зависимости от статуса и типа дохода
        if (formData.isIp) {
          // Расчет для ИП
          switch (formData.ipTaxSystem) {
            case 'osn':
              taxRate = taxRates.resident.business.osn;
              break;
            case 'usn_income':
              taxRate = taxRates.resident.business.usn_income;
              break;
            case 'usn_income_expenses':
              taxRate = taxRates.resident.business.usn_income_expenses;
              break;
            case 'eshn':
              taxRate = taxRates.resident.business.eshn;
              break;
            case 'psn':
              taxRate = taxRates.resident.business.psn;
              break;
            case 'patent':
              taxRate = taxRates.resident.business.patent;
              break;
            default:
              taxRate = taxRates.resident.business.osn;
          }
        } else if (formData.isSelfEmployed) {
          // Расчет для самозанятых
          taxRate = taxRates.resident.self_employed.individual;
        } else {
          // Расчет для обычных физических лиц
          if (formData.taxResidency === 'resident') {
            if (formData.incomeType === 'salary') {
              if (annualIncome > 5000000) {
                // Прогрессивная шкала для доходов свыше 5 млн.
                taxRate = 15;
              } else {
                // Для доходов менее 5 млн используем плоскую ставку
                taxRate = taxRates.resident.salary.flat || 13;
              }
            } else {
              // Для других типов дохода
              taxRate = typeof taxRates.resident[formData.incomeType] === 'object' 
                ? (taxRates.resident[formData.incomeType].flat || 13)
                : (taxRates.resident[formData.incomeType] || 13);
            }
          }
        }

        // Расчет базы налогообложения
        let taxableIncome = annualIncome;
        
        // Для УСН доходы-расходы
        if (formData.isIp && formData.ipTaxSystem === 'usn_income_expenses') {
          const expenses = parseFloatOrDefault(formData.ipExpenses, 0);
          taxableIncome = Math.max(0, annualIncome - expenses);
        }

        // Расчет вычетов
        let totalDeductions = 0;
        const deductionsBreakdown = {
          standard: 0,
          social: 0,
          property: 0,
          investment: 0
        };

        // Применение вычетов (только для резидентов РФ и не для самозанятых и не для спец. режимов ИП)
        if (formData.includeDeductions && 
            formData.taxResidency === 'resident' && 
            !formData.isSelfEmployed && 
            (!formData.isIp || formData.ipTaxSystem === 'osn')) {
          
          // Стандартные вычеты на детей
          if (formData.hasChildren) {
            const childrenCount = parseIntOrDefault(formData.childrenCount, 0);
            let standardDeduction = 0;
            
            // Первый и второй ребенок
            const firstSecondChildCount = Math.min(2, childrenCount);
            standardDeduction += firstSecondChildCount * deductionLimits.standard.children.regular * 12;
            
            // Третий и последующие дети
            if (childrenCount > 2) {
              standardDeduction += (childrenCount - 2) * deductionLimits.standard.children.third_plus * 12;
            }
            
            deductionsBreakdown.standard += standardDeduction;
          }

          // Вычет на детей-инвалидов
          if (formData.hasChildrenDisabled) {
            const disabledChildrenCount = parseIntOrDefault(formData.childrenDisabledCount, 0);
            const disabledChildrenDeduction = disabledChildrenCount * deductionLimits.standard.children.disabled * 12;
            deductionsBreakdown.standard += disabledChildrenDeduction;
          }

          // Социальные вычеты
          
          // ИПН
          if (formData.hasIpn) {
            const ipnAmount = Math.min(
              parseFloatOrDefault(formData.ipnAmount, 0),
              deductionLimits.social.ipn
            );
            deductionsBreakdown.social += ipnAmount;
          }
          
          // Ипотечные проценты
          if (formData.hasMortgage) {
            const mortgageAmount = Math.min(
              parseFloatOrDefault(formData.mortgageInterestAmount, 0),
              deductionLimits.property.mortgage_interest
            );
            deductionsBreakdown.property += mortgageAmount;
          }
          
          // Обучение
          if (formData.hasEducation) {
            const educationAmount = Math.min(
              parseFloatOrDefault(formData.educationExpenseAmount, 0),
              deductionLimits.social.education
            );
            deductionsBreakdown.social += educationAmount;
          }
          
          // Лечение
          if (formData.hasMedical) {
            const medicalAmount = Math.min(
              parseFloatOrDefault(formData.medicalExpenseAmount, 0),
              deductionLimits.social.medical
            );
            deductionsBreakdown.social += medicalAmount;
          }
          
          // Страхование
          if (formData.hasInsurance) {
            const insuranceAmount = Math.min(
              parseFloatOrDefault(formData.insuranceExpenseAmount, 0),
              deductionLimits.social.insurance
            );
            deductionsBreakdown.social += insuranceAmount;
          }
          
          // Инвестиции
          if (formData.hasInvestment) {
            const investmentAmount = Math.min(
              parseFloatOrDefault(formData.investmentExpenseAmount, 0),
              deductionLimits.investment.iis_type_a
            );
            deductionsBreakdown.investment += investmentAmount;
          }
          
          // Благотворительность
          if (formData.hasCharity) {
            const charityAmount = Math.min(
              parseFloatOrDefault(formData.charityExpenseAmount, 0),
              annualIncome * 0.25
            );
            deductionsBreakdown.social += charityAmount;
          }
          
          // Суммируем все вычеты
          totalDeductions = 
            deductionsBreakdown.standard + 
            deductionsBreakdown.social + 
            deductionsBreakdown.property + 
            deductionsBreakdown.investment;
          
          // Ограничиваем вычеты суммой налогооблагаемого дохода
          totalDeductions = Math.min(totalDeductions, taxableIncome);
        }
        
        // Рассчитываем налогооблагаемый доход с учетом вычетов
        const taxableIncomeAfterDeductions = taxableIncome - totalDeductions;
        
        // Расчет налога для прогрессивной шкалы (для доходов свыше 5 млн руб.)
        let taxAmount = 0;
        
        if (formData.taxResidency === 'resident' && 
            formData.incomeType === 'salary' && 
            !formData.isIp && 
            !formData.isSelfEmployed && 
            annualIncome > 5000000) {
          
          // Расчет по прогрессивной шкале
          // 13% на доход до 5 млн
          const baseTaxAmount = Math.min(taxableIncomeAfterDeductions, 5000000) * (13 / 100);
          
          // 15% на доход свыше 5 млн
          const additionalTaxAmount = Math.max(0, taxableIncomeAfterDeductions - 5000000) * (15 / 100);
          
          taxAmount = baseTaxAmount + additionalTaxAmount;
        } else {
          // Расчет по плоской шкале
          taxAmount = taxableIncomeAfterDeductions * (taxRate / 100);
        }
        
        // Рассчитываем налоговую экономию
        const taxSaving = totalDeductions * (13 / 100); // Вычеты дают экономию по ставке 13%
        
        // Рассчитываем эффективную налоговую ставку
        const effectiveTaxRate = taxableIncome > 0 ? (taxAmount / taxableIncome) * 100 : 0;
        
        // Формируем результаты
        const finalResults = {
          grossIncome: annualIncome,
          taxableIncome: taxableIncomeAfterDeductions,
          totalDeductions: totalDeductions,
          taxAmount: taxAmount,
          netIncome: annualIncome - taxAmount,
          taxRate: taxRate,
          effectiveTaxRate: effectiveTaxRate,
          taxSaving: taxSaving
        };
        
        // Формируем детали расчета
        const detailedResults = {
          deductionsBreakdown: deductionsBreakdown,
          taxBreakdown: {
            base: formData.taxResidency === 'resident' && formData.incomeType === 'salary' && annualIncome > 5000000 ? 
              Math.min(taxableIncomeAfterDeductions, 5000000) * (13 / 100) : taxAmount,
            progressive: formData.taxResidency === 'resident' && formData.incomeType === 'salary' && annualIncome > 5000000 ? 
              Math.max(0, taxableIncomeAfterDeductions - 5000000) * (15 / 100) : 0
          },
          incomeBreakdown: {
            monthly: formData.paymentPeriod === 'monthly' ? numIncomeAmount : annualIncome / 12,
            annual: annualIncome,
            afterTax: {
              monthly: (annualIncome - taxAmount) / 12,
              annual: annualIncome - taxAmount
            }
          }
        };
        
        setResults(finalResults);
        setTaxDetails(detailedResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setTaxDetails(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setTaxDetails, setIsCalculating, taxRates, deductionLimits]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      incomeType: 'salary',
      incomeAmount: '1000000',
      paymentPeriod: 'annual',
      includeDeductions: false,
      taxResidency: 'resident',
      hasChildren: false,
      childrenCount: '1',
      hasChildrenDisabled: false,
      childrenDisabledCount: '0',
      hasIpn: false,
      ipnAmount: '0',
      hasMortgage: false,
      mortgageInterestAmount: '0',
      hasEducation: false,
      educationExpenseAmount: '0',
      hasMedical: false,
      medicalExpenseAmount: '0',
      hasInsurance: false,
      insuranceExpenseAmount: '0',
      hasInvestment: false,
      investmentExpenseAmount: '0',
      hasCharity: false,
      charityExpenseAmount: '0',
      isIp: false,
      ipTaxSystem: 'osn',
      ipExpenses: '0',
      isSelfEmployed: false,
      region: 'moscow',
      year: '2024'
    });
    setResults(null);
    setTaxDetails(null);
    setErrors({});
    setSaveCalcName('');
    setShowSaveDialog(false);
  };

  // Экспорт расчетов в CSV
  const exportToCSV = () => {
    if (!results || !taxDetails) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    // Основные данные расчета
    const mainData = [
      ['Калькулятор НДФЛ', 'Дата расчета: ' + new Date().toLocaleDateString('ru-RU')],
      [''],
      ['Параметры расчета:'],
      ['Тип дохода:', incomeTypes.find(type => type.id === formData.incomeType)?.name || formData.incomeType],
      ['Сумма дохода:', formatCsvNumber(results.grossIncome) + ' ₽'],
      ['Период:', formData.paymentPeriod === 'annual' ? 'Годовой' : formData.paymentPeriod === 'monthly' ? 'Ежемесячный' : 'Дневной'],
      ['Налоговый статус:', formData.taxResidency === 'resident' ? 'Налоговый резидент РФ' : 'Нерезидент'],
      ['Применены вычеты:', formData.includeDeductions ? 'Да' : 'Нет'],
      ['ИП:', formData.isIp ? 'Да' : 'Нет'],
      ['Самозанятый:', formData.isSelfEmployed ? 'Да' : 'Нет'],
      [''],
      ['Результаты расчета:'],
      ['Налогооблагаемый доход:', formatCsvNumber(results.taxableIncome) + ' ₽'],
      ['Общая сумма вычетов:', formatCsvNumber(results.totalDeductions) + ' ₽'],
      ['Сумма НДФЛ:', formatCsvNumber(results.taxAmount) + ' ₽'],
      ['Чистый доход после уплаты налога:', formatCsvNumber(results.netIncome) + ' ₽'],
      ['Ставка НДФЛ:', results.taxRate + '%'],
      ['Эффективная налоговая ставка:', formatCsvNumber(results.effectiveTaxRate) + '%'],
      ['Экономия на налогах благодаря вычетам:', formatCsvNumber(results.taxSaving) + ' ₽'],
      [''],
    ];

    // Детали вычетов (если применены)
    let deductionsData = [];
    if (formData.includeDeductions && results.totalDeductions > 0) {
      deductionsData = [
        ['Детализация вычетов:'],
        ['Стандартные вычеты:', formatCsvNumber(taxDetails.deductionsBreakdown.standard) + ' ₽'],
        ['Социальные вычеты:', formatCsvNumber(taxDetails.deductionsBreakdown.social) + ' ₽'],
        ['Имущественные вычеты:', formatCsvNumber(taxDetails.deductionsBreakdown.property) + ' ₽'],
        ['Инвестиционные вычеты:', formatCsvNumber(taxDetails.deductionsBreakdown.investment) + ' ₽'],
        ['']
      ];
    }

    // Детали дохода
    const incomeData = [
      ['Детализация дохода:'],
      ['Ежемесячный доход (до налога):', formatCsvNumber(taxDetails.incomeBreakdown.monthly) + ' ₽'],
      ['Годовой доход (до налога):', formatCsvNumber(taxDetails.incomeBreakdown.annual) + ' ₽'],
      ['Ежемесячный доход (после налога):', formatCsvNumber(taxDetails.incomeBreakdown.afterTax.monthly) + ' ₽'],
      ['Годовой доход (после налога):', formatCsvNumber(taxDetails.incomeBreakdown.afterTax.annual) + ' ₽'],
      ['']
    ];

    // Объединяем все данные
    const allData = [
      ...mainData,
      ...deductionsData,
      ...incomeData
    ];

    // Формируем CSV контент
    const csvContent = '\uFEFF' + allData.map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Создаем ссылку для скачивания
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_ндфл.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalcName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalcName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateIncomeTax();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры расчета НДФЛ
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры дохода */}
        <IncomeParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          incomeTypes={incomeTypes}
        />

        {/* Статус налогоплательщика */}
        <TaxResidencySection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        {/* Предприниматель */}
        <EntrepreneurSection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        {/* Самозанятый */}
        <SelfEmployedSection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        {/* Вычеты */}
        <DeductionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          deductionLimits={deductionLimits}
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalcName}
                onChange={(e) => setSaveCalcName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать НДФЛ'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;